/*
MIT License

Copyright (c) 2017

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
*/

/*eslint-disable*/
import { getFileExtension } from '@/utils/torrent';

const SUPPORTED_FORMATS = ['vtt', 'lrc', 'smi', 'ssa', 'ass', 'sub', 'srt', 'sbv', 'json'];
const SUPPORTED_EXTENSIONS = SUPPORTED_FORMATS.map(i => `.${i}`);

export const FORMAT = {};

function clone(obj) {
    return JSON.parse(JSON.stringify(obj));
}

/******************************************************************************************
 * Loads the subtitle format parsers and builders
 ******************************************************************************************/
(function init() {
    //Load in the predefined order
    for (var i = 0; i < SUPPORTED_FORMATS.length; i++) {
        var f = SUPPORTED_FORMATS[i];
        var handler = require('./format/' + f + '.js');
        FORMAT[handler.name] = handler;
    }
})();

/******************************************************************************************
 * Gets a list of supported subtitle formats.
 ******************************************************************************************/
export const list = () => {
    return Object.keys(FORMAT);
};

/******************************************************************************************
 * Detects a subtitle format from the content.
 ******************************************************************************************/
export const detect = (content) => {
    var formats = list();
    for (var i = 0; i < formats.length; i++) {
        var f = formats[i];
        var handler = FORMAT[f];
        if (typeof handler == 'undefined') {
            continue;
        }
        if (typeof handler.detect != 'function') {
            continue;
        }
        //Function 'detect' can return true or format name
        try {
            var d = handler.detect(content);
            if (d === true) {
                //Logical true
                return f;
            }
            if (f == d) {
                //Format name
                return d;
            }
        } catch (err) {
            //ignore
        }
    }
};

/******************************************************************************************
 * Parses a subtitle content.
 ******************************************************************************************/
export const parse = (content, options) => {
    options = options || {};
    var format = options.format || detect(content);
    if (!format || format.trim().length == 0) {
        throw new Error('Cannot determine subtitle format!');
    }

    var handler = FORMAT[format];
    if (typeof handler == 'undefined') {
        throw new Error('Unsupported subtitle format: ' + format);
    }

    var func = handler.parse;
    if (typeof func != 'function') {
        throw new Error("Subtitle format does not support 'parse' op: " + format);
    }

    return func(content, options);
};

/******************************************************************************************
 * Builds a subtitle content
 ******************************************************************************************/
export const build = (captions, options) => {
    options = options || {};
    var format = options.format || 'srt';
    if (!format || format.trim().length == 0) {
        throw new Error('Cannot determine subtitle format!');
    }

    var handler = FORMAT[format];
    if (typeof handler == 'undefined') {
        throw new Error('Unsupported subtitle format: ' + format);
    }

    var func = handler.build;
    if (typeof func != 'function') {
        throw new Error("Subtitle format does not support 'build' op: " + format);
    }

    return func(captions, options);
};

/******************************************************************************************
 * Converts subtitle format
 ******************************************************************************************/
export const convert = (content, options) => {
    if (typeof options == 'string') {
        options = { to: options };
    }
    options = options || {};

    var opt = clone(options);
    delete opt.format;

    if (opt.from) {
        opt.format = opt.from;
    }

    var captions = parse(content, opt);
    if (opt.resync) {
        captions = resync(captions, opt.resync);
    }

    opt.format = opt.to || options.format;
    var result = build(captions, opt);

    return result;
};

/******************************************************************************************
 * Shifts the time of the captions.
 ******************************************************************************************/
export const resync = (captions, options) => {
    options = options || {};

    var func, ratio, frame, offset;
    if (typeof options == 'function') {
        func = options; //User's function to handle time shift
    } else if (typeof options == 'number') {
        offset = options; //Time shift (+/- offset)
        func = function (a) {
            return [a[0] + offset, a[1] + offset];
        };
    } else if (typeof options == 'object') {
        offset = (options.offset || 0) * (options.frame ? options.fps || 25 : 1);
        ratio = options.ratio || 1.0;
        frame = options.frame;
        func = function (a) {
            return [Math.round(a[0] * ratio + offset), Math.round(a[1] * ratio + offset)];
        };
    } else {
        throw new Error("Argument 'options' not defined!");
    }

    var resynced = [];
    for (var i = 0; i < captions.length; i++) {
        var caption = clone(captions[i]);
        if (typeof caption.type === 'undefined' || caption.type == 'caption') {
            if (frame) {
                var shift = func([caption.frame.start, caption.frame.end]);
                if (shift && shift.length == 2) {
                    caption.frame.start = shift[0];
                    caption.frame.end = shift[1];
                    caption.frame.count = caption.frame.end - caption.frame.start;
                }
            } else {
                var shift = func([caption.start, caption.end]);
                if (shift && shift.length == 2) {
                    caption.start = shift[0];
                    caption.end = shift[1];
                    caption.duration = caption.end - caption.start;
                }
            }
        }
        resynced.push(caption);
    }

    return resynced;
};

export const supportedSubtitleExtension = (fileName) => {
    return SUPPORTED_EXTENSIONS.includes(getFileExtension(fileName).toLowerCase());
};
